import { useEffect, useRef, useState } from 'react'

/** Number of characters to reveal per tick */
export const CHARS_PER_TICK = 3

/** Milliseconds between ticks */
export const TICK_INTERVAL_MS = 20

interface UseFakeStreamParams {
  text: string
}

interface UseFakeStreamReturn {
  displayedText: string
  isComplete: boolean
}

/**
 * Simulates LLM-style streaming by progressively revealing text.
 *
 * - On first mount, streams `text` character-by-character at ~CHARS_PER_TICK
 *   every TICK_INTERVAL_MS.
 * - Once complete, `hasStreamedRef` prevents re-streaming if the component
 *   stays mounted (e.g. panel close/reopen via CSS).
 * - To re-stream for a new video, remount the component by changing its React `key`.
 */
export function useFakeStream ({ text }: UseFakeStreamParams): UseFakeStreamReturn {
  const hasStreamedRef = useRef(false)
  const [charCount, setCharCount] = useState(() => (hasStreamedRef.current ? text.length : 0))

  useEffect(() => {
    // If already streamed once, show full text immediately
    if (hasStreamedRef.current) {
      setCharCount(text.length)
      return undefined
    }

    if (!text) return undefined

    const id = setInterval(() => {
      setCharCount((prev) => {
        const next = prev + CHARS_PER_TICK
        if (next >= text.length) {
          clearInterval(id)
          hasStreamedRef.current = true
          return text.length
        }
        return next
      })
    }, TICK_INTERVAL_MS)

    return () => clearInterval(id)
  }, [text])

  return {
    displayedText: text.slice(0, charCount),
    isComplete: charCount >= text.length,
  }
}
